// static class for operations on objects
// it will offer deep copy of objects
// it will offer deep copy of arrays

export class DeepCopyUtil {
	static deepCopyArray<T>(
		arr: T[],
		ignoreList: string[] = [],
		endingIgnoreList: string[] = [],
		requiredIncludeList: string[] = [],
	): (T | Date | undefined)[] {
		return arr.map((item) =>
			DeepCopyUtil.deepCopyObject<T>(
				item,
				ignoreList,
				endingIgnoreList,
				requiredIncludeList,
			),
		);
	}

	static deepCopyObject<T>(
		obj: T,
		ignoreList: string[] = [],
		endingIgnoreList: string[] = [],
		requiredIncludeList: string[] = [],
	): T | undefined | Date {
		if (obj === null || obj === undefined) return obj;

		if (typeof obj === "function") return undefined;

		if (typeof obj !== "object" || obj instanceof Date) {
			return obj instanceof Date ? new Date(obj.getTime()) : obj;
		}

		const newObj: any = Array.isArray(obj) ? [] : {};

		for (const prop in obj) {
			if (
				DeepCopyUtil.shouldIgnoreProperty(
					obj,
					prop,
					ignoreList,
					endingIgnoreList,
					requiredIncludeList,
				)
			) {
				continue;
			}

			newObj[prop] =
				typeof obj[prop] === "object"
					? DeepCopyUtil.deepCopyObject(
							obj[prop],
							ignoreList,
							endingIgnoreList,
							requiredIncludeList,
						)
					: obj[prop];
		}

		return newObj as T; // Ensure the result has the correct type
	}

	private static shouldIgnoreProperty(
		obj: any,
		prop: string,
		ignoreList: string[],
		endingIgnoreList: string[],
		requiredIncludeList: string[],
	): boolean {
		return (
			ignoreList.includes(prop) ||
			endingIgnoreList.some((i) => prop.endsWith(i)) ||
			obj[prop] === null ||
			obj[prop] === undefined ||
			obj[prop] === "" ||
			obj[prop] === false ||
			(!requiredIncludeList.includes(prop) && obj[prop] < 1) ||
			(Array.isArray(obj[prop]) && obj[prop].length === 0) ||
			(typeof obj[prop] === "object" &&
				!(obj[prop] instanceof Date) &&
				Object.keys(obj[prop]).length === 0)
		);
	}
}

// Custom stringify function that avoids circular references and skips specific properties
const customStringify = (obj: any): string => {
	const seen = new WeakSet();

	return JSON.stringify(obj, (key, value) => {
		if (key === "_zoneDelegate" || key === "zone") {
			return undefined; // Exclude these properties
		}

		// Skip circular references
		if (typeof value === "object" && value !== null) {
			if (seen.has(value)) {
				return undefined;
			}
			seen.add(value);
		}
		return value;
	});
};

class ObjectsOperations {
	static stringify(obj: any): string {
		return customStringify(obj);
	}

	static deepCopyNestedObjectExpensive(obj: any) {
		const safeStringifiedObject = customStringify(obj);
		const parsedObject = JSON.parse(safeStringifiedObject);
		return parsedObject;
	}

	static deepCopyPropertyObject(obj: any) {
		return { ...obj };
	}

	static typeSafeStructuredClone<T>(obj: any): T {
		return structuredClone(obj);
	}

	static deepCopyArray(arr: any[], ignoreList: string[] = []) {
		// loop through array
		// use recursion to deep copy objects
		// if object is an array, call deepCopyArray
		// if object is an object, call deepCopyObject
		// if object is a instance of Date, return new Date(obj)
		// if object is a primitive, return object
		// if object is null, return null
		// if object is undefined, return undefined
		const newArr: any[] = [];
		for (const item of arr) {
			newArr.push(ObjectsOperations.deepCopyObjects(item, ignoreList));
		}
		return newArr;
	}

	static deepCopyObjects(
		obj: any,
		ignoreList: string[] = [],
		endingIgnoreList: string[] = [],
		requiredIncludeList: string[] = [],
	) {
		// loop through object properties
		// use recursion to deep copy objects
		// if object is an array, call deepCopyArray
		// if object is an object, call deepCopyObject
		// if object is a instance of Date, return new Date(obj)
		// if object is a primitive, return object
		// if object is null, return null
		// if object is undefined, return undefined
		// if object is one of the ignoreList, return
		if (obj === null) return null;
		if (typeof obj === "undefined" || obj === undefined) {
			return undefined;
		} else if (typeof obj === "function") {
			return undefined;
		} else if (obj instanceof Date) {
			return new Date(obj.getTime());
		} else if (obj instanceof Array) {
			return ObjectsOperations.deepCopyArray(obj);
		} else if (typeof obj !== "object") {
			return obj;
		}
		const newObj: { [key: string]: any } = {};
		for (const prop in obj) {
			if (
				ignoreList.includes(prop) ||
				endingIgnoreList.some((i) => prop.endsWith(i)) ||
				obj[prop] === null ||
				obj[prop] === undefined ||
				obj[prop] === "" ||
				obj[prop] === false ||
				(obj[prop] < 1 && !requiredIncludeList.includes(prop)) || // is less than 1 and not in requiredIncludeList
				(Array.isArray(obj[prop]) && obj[prop].length === 0) || // is empty array
				(typeof obj[prop] === "object" &&
					!(obj[prop] instanceof Date) &&
					Object.keys(obj[prop]).length === 0) // is empty object
			) {
				continue;
			}
			if (Object.hasOwn(obj, prop)) {
				if (obj[prop] instanceof Date) {
					newObj[prop] = new Date(obj[prop].getTime());
				} else if (typeof obj[prop] === "object") {
					newObj[prop] = ObjectsOperations.deepCopyObjects(
						obj[prop],
						ignoreList,
						endingIgnoreList,
						requiredIncludeList,
					);
				} else {
					newObj[prop] = obj[prop];
				}
			}
		}
		return newObj;
	}

	static deepCopyObjectsAll(obj: any) {
		// loop through object properties
		// use recursion to deep copy objects
		// if object is an array, call deepCopyArray
		// if object is an object, call deepCopyObject
		// if object is a instance of Date, return new Date(obj)
		// if object is a primitive, return object
		// if object is null, return null
		// if object is undefined, return undefined
		// if object is one of the ignoreList, return
		if (obj === null) return null;
		if (typeof obj === "undefined") {
			return undefined;
		} else if (obj instanceof Date) {
			return new Date(obj.getTime());
		} else if (obj instanceof Array) {
			return ObjectsOperations.deepCopyArray(obj);
		} else if (typeof obj !== "object") {
			return obj;
		}
		const newObj: { [key: string]: any } = {};
		for (const prop in obj) {
			if (Object.hasOwn(obj, prop)) {
				if (typeof obj[prop] === "object") {
					newObj[prop] = ObjectsOperations.deepCopyObjectsAll(obj[prop]);
				} else {
					newObj[prop] = obj[prop];
				}
			}
		}
		return newObj;
	}
}

// decalre new window global object
declare global {
	interface Window {
		Operations: ObjectsOperations;
	}
}

export default ObjectsOperations;

// const benchmark = () => {
//   // geerate 1000 objects

//   const objects = [];
//   for (let i = 0; i < 10000; i++) {
//     objects.push(nestedDeeplyMixed());
//   }

//   // benchmark
//   console.time("typeSafeStructuredClone");
//   for (const obj of objects) {
//     ObjectsOperations.typeSafeStructuredClone(obj);
//   }
//   console.timeEnd("typeSafeStructuredClone");

//   console.time("deepCopyObjects");
//   for (const obj of objects) {
//     ObjectsOperations.deepCopyObjects(obj);
//   }

//   console.timeEnd("deepCopyObjects");

//   console.time("deepCopyObjectsAll");
//   for (const obj of objects) {
//     ObjectsOperations.deepCopyObjectsAll(obj);
//   }

//   console.timeEnd("deepCopyObjectsAll");

//   //deepCopyNestedObjectExpensive

//   console.time("deepCopyNestedObjectExpensive");
//   for (const obj of objects) {
//     ObjectsOperations.deepCopyNestedObjectExpensive(obj);
//   }

//   console.timeEnd("deepCopyNestedObjectExpensive");

// }

// // declare on window
// (window as any).Benchmark = benchmark;
